// Pure data helpers for the RPM list thumbnail (Infograph). No React /
// Chart.js imports so they can be checked with plain node.
//
// The thumbnail shows month-by-month progression: ONE point per calendar
// month = the average of that month's readings, labelled once ("Aug", "Sep",
// "Oct"). Months with no readings are skipped. It used to plot the last four
// raw readings and label each with its month name, so a card read
// "October October October October".
//
// Timestamps are parsed the way the chart RPM tab parses them
// (parseRpmTimestamp: local time, Safari-safe); values must be finite numbers.
import { parseRpmTimestamp } from "../../graphs/linegraphs/rpmsittings";

const SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const LONG = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// bpp-master returns at most this many readings (newest first).
export const BPP_MASTER_CAP = 50;
// Most months a thumbnail shows (the newest ones).
export const THUMBNAIL_MONTHS = 6;

const toFiniteNumber = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const pad2 = (n) => String(n).padStart(2, "0");
export const monthKeyOf = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;

// Weight keeps one decimal; BP / pulse / glucose are whole numbers.
const roundFor = (key, n) => (key === "weight" ? Math.round(n * 10) / 10 : Math.round(n));

// readings: [{ datetime, systolic, diastolic, pulse, weight, ... }], any order.
// keys: the value fields to average, e.g. ["systolic", "diastolic"].
// Returns the newest `maxMonths` months that have readings, oldest first:
//   { key: "2026-10", label: "Oct", title: "October 2026", count, values: { systolic: 138, ... } }
// count = readings in that month with at least one of `keys`. When the months
// span more than one year the label carries the year ("Dec '25", "Jan '26").
export function buildMonthlyAverages(readings, keys, { maxMonths = THUMBNAIL_MONTHS } = {}) {
  const buckets = new Map();
  (readings || []).forEach((r) => {
    if (!r) return;
    const d = parseRpmTimestamp(r.datetime);
    if (!d) return;
    const nums = {};
    let any = false;
    keys.forEach((k) => {
      const n = toFiniteNumber(r[k]);
      if (n !== null) {
        nums[k] = n;
        any = true;
      }
    });
    if (!any) return;
    const key = monthKeyOf(d);
    let b = buckets.get(key);
    if (!b) {
      b = { key, year: d.getFullYear(), month: d.getMonth(), count: 0, sums: {}, ns: {} };
      buckets.set(key, b);
    }
    b.count += 1;
    Object.entries(nums).forEach(([k, n]) => {
      b.sums[k] = (b.sums[k] || 0) + n;
      b.ns[k] = (b.ns[k] || 0) + 1;
    });
  });

  let months = Array.from(buckets.values()).sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
  if (maxMonths && months.length > maxMonths) months = months.slice(-maxMonths);
  const multiYear = new Set(months.map((m) => m.year)).size > 1;

  return months.map((m) => {
    const values = {};
    keys.forEach((k) => {
      values[k] = m.ns[k] ? roundFor(k, m.sums[k] / m.ns[k]) : null;
    });
    return {
      key: m.key,
      label: multiYear ? `${SHORT[m.month]} '${String(m.year).slice(-2)}` : SHORT[m.month],
      title: `${LONG[m.month]} ${m.year}`,
      count: m.count,
      values,
    };
  });
}

// bpp-master's `data` block (columnar arrays, newest first) -> readings.
// `datetime` is the reading time to the second; `datelabel` is the fallback.
export function bppMasterToReadings(data) {
  const d = data || {};
  const stamps = Array.isArray(d.datetime) ? d.datetime : [];
  const labels = Array.isArray(d.datelabel) ? d.datelabel : [];
  const n = Math.max(stamps.length, labels.length);
  const out = [];
  for (let i = 0; i < n; i += 1) {
    out.push({
      datetime: stamps[i] || labels[i],
      systolic: d.systolic?.[i],
      diastolic: d.diastolic?.[i],
      pulse: d.pulse?.[i],
    });
  }
  return out;
}

const readingKey = (r) => {
  const d = parseRpmTimestamp(r.datetime);
  return [d ? d.getTime() : String(r.datetime), r.systolic, r.diastolic, r.weight]
    .map((v) => (v === undefined || v === null ? "" : String(v)))
    .join("|");
};

// The thumbnail's BP readings: the card's own BPP list (rpm/get: every
// reading since the 1st of this month) plus bpp-master's newest 50 readings
// for the earlier months. A reading in both counts once.
//
// When bpp-master hit its 50 cap, its oldest month is most likely cut off
// part-way, so that month is left out (its average would only cover the end
// of the month) unless the card's own list covers it.
export function buildThumbnailReadings(cardReadings, master, { cap = BPP_MASTER_CAP } = {}) {
  const card = Array.isArray(cardReadings) ? cardReadings.filter(Boolean) : [];
  let fromMaster = Array.isArray(master?.readings) ? master.readings.filter(Boolean) : [];

  const capped = Number(master?.totalcount || fromMaster.length) >= cap;
  if (capped && fromMaster.length) {
    const months = fromMaster
      .map((r) => parseRpmTimestamp(r.datetime))
      .filter(Boolean)
      .map(monthKeyOf)
      .sort();
    const oldest = months[0];
    const cardMonths = new Set(
      card.map((r) => parseRpmTimestamp(r.datetime)).filter(Boolean).map(monthKeyOf)
    );
    if (oldest && !cardMonths.has(oldest)) {
      fromMaster = fromMaster.filter((r) => {
        const d = parseRpmTimestamp(r.datetime);
        return !d || monthKeyOf(d) !== oldest;
      });
    }
  }

  const seen = new Set();
  const out = [];
  [...card, ...fromMaster].forEach((r) => {
    const k = readingKey(r);
    if (seen.has(k)) return;
    seen.add(k);
    out.push(r);
  });
  return out;
}
